'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["top", "toolbarHeight"];
import { memo } from 'react';
import PropTypes from 'prop-types';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { memoize } from 'underscore';
import { OLAF, SORBET } from 'HubStyleTokens/colors';
import CalypsoHandleButtonWrapper from 'layout-dnd-components/DndToolbar/CalypsoHandleButtonWrapper';
import DangerHandleButtonWrapper from 'layout-dnd-components/DndToolbar/DangerHandleButtonWrapper';
import PinnedToolbar from 'layout-dnd-components/DndToolbar/PinnedToolbar';
import { cloneModuleFromLayoutSection, requestCloneFromLayoutSection, requestModuleDelete, setBreakpointStylesForTreeNode, deleteModuleFromLayoutSection } from 'ContentEditorUI/redux/actions/moduleActions';
import { logLayoutSectionEvent } from 'InpageEditorUI/eventsAsActions/layoutTransitionEventsAsActions';
import { updateSelectedItem } from 'ContentEditorUI/redux/actions/selectionActions';
import { updateInpageMode } from 'ContentEditorUI/redux/actions/inpageActions';
import WidgetIcon from 'ContentComponents/cmv2/WidgetIcon';
import DefaultModuleIcon from './DefaultModuleIcon';
import GlobalIcon from './GlobalIcon';
import { PINNED_TOOLBAR_ICON_SIZE } from 'layout-dnd-components/DndToolbar/constants';
import { MODULE } from 'ContentEditorUI/constants/cloneTypes';
import { openConfirmEditGlobal } from 'ContentEditorUI/redux/actions/appActions';
import { hashFunctionForObject, styledHandleButtonComponent, styledIconButtonComponent, wrapHandle } from './helpers';
import { InpageModes } from 'ContentEditorUI/lib/app/constants';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { LAYOUT_SECTION_TRACKING, LAYOUT_SECTION_TRACKING_MESSAGES, DRAG_AND_DROP_AREA_ACTION } from 'ContentEditorUI/lib/layoutSection/constants';
import CommentPopOverWrapper from 'InpageEditorUI/containers/CommentPopOverWrapper';
import { toggleCollaborationSidebarCommentPopover } from 'ContentEditorUI/redux/actions/collaborationSidebarActions';
import { jsx as _jsx } from "react/jsx-runtime";
export const buildModuleEditAction = ({
  moduleId,
  isEditable,
  isGlobal,
  bus,
  dispatch,
  moduleType,
  isInNonDefaultBreakpointMode,
  shouldNotOpenSidebarOnEditClick
}) => {
  let tooltipKey = `inpage.actionToolbar.module.`;
  let placements = {
    placement: 'top left'
  };
  if (isInNonDefaultBreakpointMode) {
    tooltipKey += 'cantEditMobile';
    placements = {
      placement: 'bottom left',
      autoPlacement: 'vert'
    };
  } else {
    if (isEditable) {
      tooltipKey += 'edit';
    } else if (isGlobal) {
      tooltipKey += 'cantEditGlobal';
    } else {
      tooltipKey += 'cantEdit';
    }
  }
  if (isGlobal) {
    placements.placement = 'bottom left';
  }
  return Object.assign({
    id: 'module-edit',
    icon: 'edit',
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.edit"
    }),
    tooltipLabel: I18n.text(tooltipKey),
    disabled: !isEditable || isInNonDefaultBreakpointMode
  }, placements, {
    callback: () => {
      if (isGlobal) {
        dispatch(openConfirmEditGlobal(moduleId));
      } else {
        const options = shouldNotOpenSidebarOnEditClick ? {
          doNotShowSidebarForm: true,
          clearOutPanels: true
        } : {};
        dispatch(updateSelectedItem(moduleId, options));
      }
      bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.EDIT_MODULE, {
        moduleType
      }));
    }
  });
};
export const buildActionsForModuleInDndArea = memoize(({
  moduleId,
  layoutSectionId,
  isInNonDefaultBreakpointMode,
  activeBreakpointName,
  canBeCloned,
  moduleType,
  dispatch,
  bus,
  isGlobal,
  isEditable,
  isAdvancedModuleWithoutAccess,
  isPaymentsModuleWithoutAccess,
  hasPersonalizationTokenWithoutAccess,
  isCommentable = false
}) => {
  const moduleEditAction = buildModuleEditAction({
    moduleId,
    isEditable,
    isGlobal,
    bus,
    dispatch,
    moduleType,
    isInNonDefaultBreakpointMode
  });
  const menuLabel = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "inpage.actionToolbar.module.clone"
  });
  let menuTooltip = undefined;
  let tooltipLabel = menuLabel;
  if (!canBeCloned) {
    menuTooltip = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.cantCloneDetail"
    });
    tooltipLabel = /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.cantCloneWithDetail"
    });
  }
  const moduleDuplicateAction = {
    id: 'module-duplicate',
    icon: 'duplicate',
    menuLabel,
    menuTooltip,
    tooltipLabel,
    disabled: !canBeCloned,
    callback: () => {
      bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.CLONE_CELL));
      dispatch(requestCloneFromLayoutSection(moduleId, layoutSectionId, MODULE, cloneModuleFromLayoutSection(moduleId, layoutSectionId)));
    },
    placement: 'top left'
  };
  const moduleDeleteAction = {
    id: 'module-delete',
    icon: 'delete',
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.delete"
    }),
    tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.delete"
    }),
    callback: () => {
      UsageTracker.trackEditorInteraction({
        action: DRAG_AND_DROP_AREA_ACTION,
        event: LAYOUT_SECTION_TRACKING_MESSAGES[LAYOUT_SECTION_TRACKING.DELETE_MODULE]
      });
      dispatch(deleteModuleFromLayoutSection(moduleId, layoutSectionId));
    },
    placement: 'top left'
  };
  const placements = isInNonDefaultBreakpointMode ? {
    placement: isInNonDefaultBreakpointMode ? 'bottom left' : '',
    autoPlacement: isInNonDefaultBreakpointMode ? 'vert' : ''
  } : {};
  const hideOnMobileAction = Object.assign({
    id: 'module-hide',
    icon: 'hide',
    iconInstanceSize: 13
  }, placements, {
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.hide"
    }),
    tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.hide"
    }),
    callback: () => {
      bus.dispatchActionToEventBus(logLayoutSectionEvent(LAYOUT_SECTION_TRACKING.HIDE_MODULE));
      dispatch(setBreakpointStylesForTreeNode(layoutSectionId, moduleId, {
        hidden: true
      }, activeBreakpointName));
    },
    placement: 'top left'
  });
  const moduleCommentAction = {
    id: 'module-comment',
    icon: 'comments',
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.comment"
    }),
    tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.comment"
    }),
    callback: () => {
      dispatch(toggleCollaborationSidebarCommentPopover({
        id: moduleId
      }));
    },
    placement: 'top left'
  };
  if (isInNonDefaultBreakpointMode) {
    const buttonActions = [moduleEditAction, hideOnMobileAction];
    if (isCommentable) {
      buttonActions.push(moduleCommentAction);
    }
    return buttonActions;
  } else if (isAdvancedModuleWithoutAccess || isPaymentsModuleWithoutAccess || hasPersonalizationTokenWithoutAccess) {
    if (moduleType === 'rich_text' || moduleType === 'video' || hasPersonalizationTokenWithoutAccess) {
      return [moduleEditAction, moduleDeleteAction, moduleCommentAction];
    } else {
      return [moduleDeleteAction, moduleCommentAction];
    }
  } else {
    const buttonActions = [moduleEditAction, moduleDuplicateAction, moduleDeleteAction];
    if (isCommentable) {
      buttonActions.push(moduleCommentAction);
    }
    return buttonActions;
  }
}, hashFunctionForObject);
export const buildActionsForModuleOutsideContainer = memoize(({
  moduleId,
  moduleType,
  isInNonDefaultBreakpointMode,
  dispatch,
  bus,
  isGlobal,
  isEditable,
  shouldNotOpenSidebarOnEditClick
}) => {
  const editAction = buildModuleEditAction({
    moduleId,
    isGlobal,
    isEditable,
    bus,
    dispatch,
    moduleType,
    isInNonDefaultBreakpointMode,
    shouldNotOpenSidebarOnEditClick
  });
  return [editAction];
}, hashFunctionForObject);
export const buildActionsForModuleInFlexColumn = memoize(({
  moduleId,
  moduleType,
  isInNonDefaultBreakpointMode,
  dispatch,
  bus,
  isGlobal,
  isEditable,
  isAdvancedModuleWithoutAccess,
  isPaymentsModuleWithoutAccess,
  hasPersonalizationTokenWithoutAccess
}) => {
  const placements = isInNonDefaultBreakpointMode ? {
    placement: isInNonDefaultBreakpointMode ? 'bottom left' : '',
    autoPlacement: isInNonDefaultBreakpointMode ? 'vert' : ''
  } : {
    placement: 'top left'
  };
  const moduleEditAction = buildModuleEditAction({
    moduleId,
    isGlobal,
    isEditable,
    bus,
    dispatch,
    moduleType,
    isInNonDefaultBreakpointMode
  });
  const editLayoutAction = Object.assign({
    id: 'layout-mode',
    icon: 'flexColumn'
  }, placements, {
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.editLayout"
    }),
    tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.editLayout"
    }),
    callback: () => {
      dispatch(updateInpageMode(InpageModes.LAYOUT));
    }
  });
  const deleteAction = Object.assign({
    id: 'module-delete-in-flex-column',
    icon: 'delete'
  }, placements, {
    menuLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.delete"
    }),
    tooltipLabel: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "inpage.actionToolbar.module.delete"
    }),
    callback: () => {
      dispatch(requestModuleDelete(moduleId));
    }
  });
  if (isAdvancedModuleWithoutAccess || isPaymentsModuleWithoutAccess || hasPersonalizationTokenWithoutAccess) {
    if (moduleType === 'rich_text' || hasPersonalizationTokenWithoutAccess) {
      return [moduleEditAction, deleteAction];
    } else {
      return [deleteAction];
    }
  } else {
    return [moduleEditAction, editLayoutAction, deleteAction];
  }
}, hashFunctionForObject);
const ModuleIconWithFallback = /*#__PURE__*/memo(({
  moduleIcon,
  isGlobal
}) => {
  if (isGlobal) {
    return GlobalIcon;
  }
  if (moduleIcon) {
    return /*#__PURE__*/_jsx(WidgetIcon, {
      size: PINNED_TOOLBAR_ICON_SIZE,
      color: OLAF,
      icon: moduleIcon
    });
  }
  return DefaultModuleIcon;
});
const SorbetHandleButtonWrapper = styled.div.withConfig({
  displayName: "BaseResponsiveToolbar__SorbetHandleButtonWrapper",
  componentId: "vsa81o-0"
})([".hs-private.hs-private.hs-private & .uiButton.uiButton{background-color:", ";border:none;}.hs-private.hs-private.hs-private & .uiButton.uiButton:hover{background-color:", ";border:none;}"], SORBET, SORBET);
export const BaseResponsiveToolbar = ({
  moduleIcon,
  style,
  actions,
  isGlobal,
  isDraggable = true,
  handleTooltip,
  responsiveSize,
  onHandleMouseDown,
  onDropdownOpenChange,
  connectDragSource,
  showHandleGrippie,
  isAdvancedModuleWithoutAccess,
  isPaymentsModuleWithoutAccess,
  hasPersonalizationTokenWithoutAccess,
  isSelectedItem,
  showEditModuleOnSelected = false,
  moduleId,
  isCommentable
}) => {
  const _ref = style || {},
    {
      top,
      toolbarHeight
    } = _ref,
    restStyles = _objectWithoutPropertiesLoose(_ref, _excluded);
  const buttonActions = [...actions];
  const editActionIndex = buttonActions.findIndex(action => action.id === 'module-edit');

  // Assume edit action button's callback should be used when clicking on the "icon" of this toolbar
  const iconClickHandler = editActionIndex === -1 || buttonActions[editActionIndex].disabled ? null : buttonActions[editActionIndex].callback;
  if (!showEditModuleOnSelected && isSelectedItem && editActionIndex !== -1) {
    buttonActions.splice(editActionIndex, 1);
  }
  let wrapperComponent;
  if (isAdvancedModuleWithoutAccess || isPaymentsModuleWithoutAccess || hasPersonalizationTokenWithoutAccess) {
    wrapperComponent = DangerHandleButtonWrapper;
  } else if (isGlobal) {
    wrapperComponent = SorbetHandleButtonWrapper;
  } else {
    wrapperComponent = CalypsoHandleButtonWrapper;
  }
  let pinLocation;
  if (top - toolbarHeight < 0) {
    pinLocation = 'top right';
  } else {
    pinLocation = isSelectedItem ? 'bottom right' : 'top right';
  }
  return /*#__PURE__*/_jsx(CommentPopOverWrapper, {
    isCommentable: isCommentable,
    id: moduleId,
    children: /*#__PURE__*/_jsx(PinnedToolbar, {
      active: true
      // pinLocation affects border radius of toolbar
      ,
      pinLocation: pinLocation,
      icon: /*#__PURE__*/_jsx(ModuleIconWithFallback, {
        moduleIcon: moduleIcon,
        isGlobal: isGlobal
      }),
      actions: buttonActions,
      renderWrappedHandle: wrapHandle(connectDragSource, true, wrapperComponent),
      onHandleMouseDown: onHandleMouseDown,
      onDropdownOpenChange: onDropdownOpenChange,
      onHandleClick: iconClickHandler,
      className: "hs-ls-responsive-toolbar hs-ls-responsive-module-toolbar",
      style: Object.assign({
        top
      }, restStyles),
      responsiveSize: responsiveSize,
      handleTooltip: handleTooltip,
      handleTooltipPlacement: isGlobal ? 'bottom left' : 'top left',
      showHandleGrippie: showHandleGrippie,
      StyledIconButton: styledIconButtonComponent,
      StyledHandleButton: styledHandleButtonComponent,
      draggable: !isGlobal && isDraggable
    })
  });
};
BaseResponsiveToolbar.propTypes = {
  moduleIcon: PropTypes.string,
  style: PropTypes.object,
  actions: PropTypes.array.isRequired,
  isGlobal: PropTypes.bool,
  isDraggable: PropTypes.bool,
  responsiveSize: PropTypes.string.isRequired,
  handleTooltip: PropTypes.string,
  onHandleMouseDown: PropTypes.func,
  onDropdownOpenChange: PropTypes.func,
  connectDragSource: PropTypes.func,
  showHandleGrippie: PropTypes.bool,
  isAdvancedModuleWithoutAccess: PropTypes.bool,
  isPaymentsModuleWithoutAccess: PropTypes.bool,
  hasPersonalizationTokenWithoutAccess: PropTypes.bool,
  isSelectedItem: PropTypes.bool,
  showEditModuleOnSelected: PropTypes.bool,
  moduleId: PropTypes.string,
  isCommentable: PropTypes.bool
};